// viator_experiences.jsx
//
// PRLocal admin dashboard section for managing curated Viator experiences.
// Three tabs:
//   1) Curadas      — live viator_experiences (CRUD)
//   2) Por revisar  — unapproved_viator_experiences (approve/reject)
//   3) Scrape Queue — scraped_viator_indexes (monitor, delete, clear)
//
// Visual style mirrors nature_metadata.jsx / location_submissions.js
// (slate/violet, Tailwind, Lucide icons, Spanish labels).

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Search, X, Loader2, Plus, Edit2, Save, RefreshCw, Trash2,
  AlertTriangle, AlertCircle, CheckCircle, Compass, Star, MapPin,
  Tag, DollarSign, ExternalLink, Eye, Sparkles,
  Inbox, ThumbsUp, XCircle, ClipboardCheck, ListChecks,
  Database, Link2, CheckSquare, Square, Eraser,
  ChevronLeft, ChevronRight,
} from 'lucide-react';
import { useViatorExperiences } from '../../authentication_components/viatorExperienceContext';

// Canonical categories — the value MUST match the app's category keys
// (components/viator_experiences_components/.../ExperiencesScreenTranslations.ts)
// so an assigned category lands in the right chip/section on the app. Labels are
// Spanish to match the rest of the dashboard.
const CATEGORY_OPTIONS = [
  { value: 'tours', label: 'Tours' },
  { value: 'water', label: 'Agua' },
  { value: 'adventure', label: 'Aventura' },
  { value: 'nature', label: 'Naturaleza' },
  { value: 'food', label: 'Comida' },
  { value: 'culture', label: 'Cultura' },
];

const categoryLabel = (value) =>
  CATEGORY_OPTIONS.find((c) => c.value === value)?.label ?? value;

const EMPTY_FORM = {
  product_code: '',
  title: '',
  short_description: '',
  cover_image_url: '',
  rating: '',
  review_count: '',
  price_from: '',
  price_currency: 'USD',
  duration_text: '',
  affiliate_url: '',
  booking_type: '',
  latitude: '',
  longitude: '',
  location_city: '',
  category: '',
  tags: '',
  is_active: true,
  is_featured: false,
  display_rank: '1000',
  margin_tier: '',
  curator_note: '',
};

const experienceToForm = (exp) => ({
  product_code: exp.product_code ?? '',
  title: exp.title ?? '',
  short_description: exp.short_description ?? '',
  cover_image_url: exp.cover_image_url ?? '',
  rating: exp.rating ?? '',
  review_count: exp.review_count ?? '',
  price_from: exp.price_from ?? '',
  price_currency: exp.price_currency ?? 'USD',
  duration_text: exp.duration_text ?? '',
  affiliate_url: exp.affiliate_url ?? '',
  booking_type: exp.booking_type ?? '',
  latitude: exp.latitude ?? '',
  longitude: exp.longitude ?? '',
  location_city: exp.location_city ?? '',
  category: exp.category ?? '',
  tags: Array.isArray(exp.tags) ? exp.tags.join(', ') : '',
  is_active: exp.is_active ?? true,
  is_featured: exp.is_featured ?? false,
  display_rank: exp.display_rank ?? '1000',
  margin_tier: exp.margin_tier ?? '',
  curator_note: exp.curator_note ?? '',
});

const formToPayload = (form) => {
  const str = (v) => {
    const t = (v ?? '').toString().trim();
    return t === '' ? null : t;
  };
  const num = (v) => {
    const t = (v ?? '').toString().trim();
    if (t === '') return null;
    const n = Number(t);
    return Number.isNaN(n) ? null : n;
  };
  const int = (v) => {
    const n = num(v);
    return n === null ? null : Math.trunc(n);
  };

  return {
    product_code: (form.product_code ?? '').trim(),
    title: (form.title ?? '').trim(),
    affiliate_url: (form.affiliate_url ?? '').trim(),
    short_description: str(form.short_description),
    cover_image_url: str(form.cover_image_url),
    rating: num(form.rating),
    review_count: int(form.review_count),
    price_from: num(form.price_from),
    price_currency: str(form.price_currency) || 'USD',
    duration_text: str(form.duration_text),
    booking_type: str(form.booking_type),
    latitude: num(form.latitude),
    longitude: num(form.longitude),
    location_city: str(form.location_city),
    category: str(form.category),
    tags: (form.tags ?? '')
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean),
    is_active: !!form.is_active,
    is_featured: !!form.is_featured,
    display_rank: int(form.display_rank) ?? 1000,
    margin_tier: int(form.margin_tier),
    curator_note: str(form.curator_note),
  };
};

// =============================================================================
// FORM DIALOG
// =============================================================================
const ExperienceFormDialog = ({ isOpen, mode, initial, onClose, onSubmit, saving, error }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [localError, setLocalError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setForm(initial ? experienceToForm(initial) : EMPTY_FORM);
      setLocalError(null);
    }
  }, [isOpen, initial]);

  if (!isOpen) return null;

  const set = (key, val) => setForm((prev) => ({ ...prev, [key]: val }));

  const handleSubmit = async () => {
    setLocalError(null);
    if (!form.product_code.trim() || !form.title.trim() || !form.affiliate_url.trim()) {
      setLocalError('Codigo de producto, titulo y URL de afiliado son obligatorios.');
      return;
    }
    await onSubmit(formToPayload(form));
  };

  const field = (label, key, opts = {}) => (
    <div>
      <label className="block text-xs font-medium text-slate-700 mb-1">
        {label} {opts.required && <span className="text-red-500">*</span>}
      </label>
      <input
        type={opts.type || 'text'}
        value={form[key]}
        onChange={(e) => set(key, e.target.value)}
        placeholder={opts.placeholder || ''}
        step={opts.step}
        disabled={saving}
        className="w-full text-sm border border-slate-300 rounded-md px-2 py-2 focus:ring-violet-500 focus:border-violet-500 disabled:bg-slate-50"
      />
    </div>
  );

  return (
    <div className="fixed inset-0 bg-slate-900 bg-opacity-75 flex items-center justify-center z-[60] p-2 sm:p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white px-4 sm:px-6 py-4 border-b border-slate-100 flex justify-between items-center z-10">
          <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            {mode === 'edit' ? <Edit2 className="h-5 w-5 text-violet-600" /> : <Plus className="h-5 w-5 text-violet-600" />}
            <span>{mode === 'edit' ? 'Editar Experiencia' : 'Nueva Experiencia Viator'}</span>
          </h3>
          <button onClick={onClose} disabled={saving} className="text-slate-400 hover:text-slate-600"><X className="h-6 w-6" /></button>
        </div>

        <div className="p-4 sm:p-6 space-y-6">
          <section>
            <h4 className="text-sm font-semibold text-violet-700 mb-3 uppercase tracking-wide">Datos de Viator</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {field('Codigo de producto', 'product_code', { required: true, placeholder: 'e.g. 12345P1' })}
              {field('Titulo', 'title', { required: true })}
            </div>
            <div className="mt-3">
              <label className="block text-xs font-medium text-slate-700 mb-1">Descripcion corta</label>
              <textarea
                value={form.short_description}
                onChange={(e) => set('short_description', e.target.value)}
                rows={2}
                disabled={saving}
                className="w-full text-sm border border-slate-300 rounded-md px-2 py-2 focus:ring-violet-500 focus:border-violet-500 disabled:bg-slate-50"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
              {field('URL de imagen (cover)', 'cover_image_url', { placeholder: 'https://...' })}
              {field('URL de afiliado', 'affiliate_url', { required: true, placeholder: 'https://...' })}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
              {field('Rating (0-5)', 'rating', { type: 'number', step: '0.1' })}
              {field('# Resenas', 'review_count', { type: 'number' })}
              {field('Precio desde', 'price_from', { type: 'number', step: '0.01' })}
              {field('Moneda', 'price_currency', { placeholder: 'USD' })}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
              {field('Duracion (texto)', 'duration_text', { placeholder: 'e.g. 3 horas' })}
              {field('Tipo de reserva', 'booking_type', { placeholder: 'instant / request' })}
            </div>
          </section>

          <section>
            <h4 className="text-sm font-semibold text-violet-700 mb-3 uppercase tracking-wide">Ubicacion</h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {field('Latitud', 'latitude', { type: 'number', step: 'any' })}
              {field('Longitud', 'longitude', { type: 'number', step: 'any' })}
              {field('Municipio', 'location_city', { placeholder: 'San Juan' })}
            </div>
          </section>

          <section>
            <h4 className="text-sm font-semibold text-violet-700 mb-3 uppercase tracking-wide">Taxonomia</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Categoria</label>
                <select
                  value={form.category}
                  onChange={(e) => set('category', e.target.value)}
                  disabled={saving}
                  className="w-full text-sm border border-slate-300 rounded-md px-2 py-2 focus:ring-violet-500 focus:border-violet-500 disabled:bg-slate-50 bg-white"
                >
                  <option value="">— Sin categoria —</option>
                  {CATEGORY_OPTIONS.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                  {form.category && !CATEGORY_OPTIONS.some((c) => c.value === form.category) && (
                    <option value={form.category}>{form.category} (actual)</option>
                  )}
                </select>
              </div>
              {field('Etiquetas (separadas por coma)', 'tags', { placeholder: 'snorkel, familia' })}
            </div>
          </section>

          <section>
            <h4 className="text-sm font-semibold text-violet-700 mb-3 uppercase tracking-wide">Curacion (interno)</h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 items-end">
              {field('Orden (rank)', 'display_rank', { type: 'number' })}
              {field('Margin tier', 'margin_tier', { type: 'number' })}
              <label className="flex items-center gap-2 text-sm text-slate-700 pb-2">
                <input type="checkbox" checked={!!form.is_active} onChange={(e) => set('is_active', e.target.checked)} disabled={saving} className="h-4 w-4 rounded text-violet-600 focus:ring-violet-500" />
                Activo
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-700 pb-2">
                <input type="checkbox" checked={!!form.is_featured} onChange={(e) => set('is_featured', e.target.checked)} disabled={saving} className="h-4 w-4 rounded text-violet-600 focus:ring-violet-500" />
                Destacado
              </label>
            </div>
            <div className="mt-3">
              <label className="block text-xs font-medium text-slate-700 mb-1">Nota del curador</label>
              <textarea
                value={form.curator_note}
                onChange={(e) => set('curator_note', e.target.value)}
                rows={2}
                disabled={saving}
                className="w-full text-sm border border-slate-300 rounded-md px-2 py-2 focus:ring-violet-500 focus:border-violet-500 disabled:bg-slate-50"
              />
            </div>
          </section>

          {(localError || error) && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-red-800 text-sm">{localError || error}</p>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-white px-4 sm:px-6 py-4 border-t border-slate-100 flex gap-3">
          <button onClick={onClose} disabled={saving} className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 disabled:opacity-50">Cancelar</button>
          <button onClick={handleSubmit} disabled={saving} className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:bg-slate-300">
            {saving ? <Loader2 className="animate-spin h-4 w-4" /> : <Save className="h-4 w-4" />}
            {mode === 'edit' ? 'Guardar cambios' : 'Crear experiencia'}
          </button>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// DETAIL DIALOG
// =============================================================================
const DetailRow = ({ label, value }) => (
  <div className="flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-2 py-1.5 border-b border-slate-50">
    <span className="text-xs font-medium text-slate-500 sm:w-40 flex-shrink-0 uppercase tracking-wide">{label}</span>
    <span className="text-sm text-slate-800 break-words">{value ?? <span className="text-slate-300">-</span>}</span>
  </div>
);

const ExperienceDetailDialog = ({ isOpen, exp, onClose }) => {
  if (!isOpen || !exp) return null;
  const money = (v, c) => (v === null || v === undefined ? null : `${c || 'USD'} ${Number(v).toFixed(2)}`);
  return (
    <div className="fixed inset-0 bg-slate-900 bg-opacity-75 flex items-center justify-center z-[60] p-2 sm:p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white px-4 sm:px-6 py-4 border-b border-slate-100 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            <Eye className="h-5 w-5 text-violet-600" /><span>Detalle de la experiencia</span>
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="h-6 w-6" /></button>
        </div>
        <div className="p-4 sm:p-6">
          {exp.cover_image_url && (
            <img src={exp.cover_image_url} alt={exp.title} className="w-full h-48 object-cover rounded-lg mb-4 border border-slate-100" />
          )}
          <DetailRow label="ID" value={exp.experience_id} />
          <DetailRow label="Codigo producto" value={exp.product_code} />
          <DetailRow label="Titulo" value={exp.title} />
          <DetailRow label="Descripcion" value={exp.short_description} />
          <DetailRow label="Rating" value={exp.rating !== null && exp.rating !== undefined ? `${exp.rating} (${exp.review_count ?? 0} resenas)` : null} />
          <DetailRow label="Precio desde" value={money(exp.price_from, exp.price_currency)} />
          <DetailRow label="Duracion" value={exp.duration_text} />
          <DetailRow label="Tipo reserva" value={exp.booking_type} />
          <DetailRow label="URL afiliado" value={exp.affiliate_url ? <a href={exp.affiliate_url} target="_blank" rel="noreferrer" className="text-violet-600 hover:underline inline-flex items-center gap-1">Abrir enlace <ExternalLink className="h-3 w-3" /></a> : null} />
          <DetailRow label="Municipio" value={exp.location_city} />
          <DetailRow label="Coordenadas" value={exp.latitude != null && exp.longitude != null ? `${exp.latitude}, ${exp.longitude}` : null} />
          <DetailRow label="Categoria" value={exp.category ? categoryLabel(exp.category) : null} />
          <DetailRow label="Etiquetas" value={Array.isArray(exp.tags) && exp.tags.length ? exp.tags.join(', ') : null} />
          <DetailRow label="Activo" value={exp.is_active ? 'Si' : 'No'} />
          <DetailRow label="Destacado" value={exp.is_featured ? 'Si' : 'No'} />
          <DetailRow label="Orden (rank)" value={exp.display_rank} />
          <DetailRow label="Margin tier" value={exp.margin_tier} />
          <DetailRow label="Nota curador" value={exp.curator_note} />
          <DetailRow label="Precio actualizado" value={exp.price_updated_at} />
          <DetailRow label="Sincronizado" value={exp.synced_at} />
          <DetailRow label="Creado" value={exp.created_at} />
          <DetailRow label="Actualizado" value={exp.updated_at} />
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// DELETE CONFIRMATION
// =============================================================================
const DeleteDialog = ({ isOpen, exp, onClose, onConfirm, deleting, error }) => {
  if (!isOpen || !exp) return null;
  return (
    <div className="fixed inset-0 bg-slate-900 bg-opacity-75 flex items-center justify-center z-[70] p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 text-center">
          <div className="mx-auto flex items-center justify-center h-14 w-14 rounded-full bg-red-50 mb-4">
            <AlertTriangle className="h-7 w-7 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Eliminar esta experiencia?</h3>
          <p className="text-slate-600 text-sm">Se eliminara permanentemente:</p>
          <p className="font-bold text-slate-800 my-2 break-words">{exp.title}</p>
          <p className="text-xs text-slate-500 font-mono">{exp.product_code}</p>
          {error && <p className="text-red-600 text-sm mt-3">{error}</p>}
          <div className="flex gap-3 mt-6">
            <button onClick={onClose} disabled={deleting} className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 disabled:opacity-50">Cancelar</button>
            <button onClick={onConfirm} disabled={deleting} className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-red-300">
              {deleting ? <Loader2 className="animate-spin h-4 w-4" /> : <Trash2 className="h-4 w-4" />}
              Eliminar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// PAGINATION CONTROLS
// =============================================================================
const ITEMS_PER_PAGE = 100;

const PaginationControls = ({ currentPage, totalPages, totalItems, onPageChange }) => {
  if (totalPages <= 1) return null;

  const start = (currentPage - 1) * ITEMS_PER_PAGE + 1;
  const end = Math.min(currentPage * ITEMS_PER_PAGE, totalItems);

  return (
    <div className="flex items-center justify-between mt-4 px-1">
      <span className="text-xs text-slate-500">
        Mostrando <strong>{start}</strong>–<strong>{end}</strong> de <strong>{totalItems}</strong>
      </span>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1}
          className="flex items-center gap-1 px-3 py-1.5 text-xs border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
          Anterior
        </button>
        <span className="px-3 py-1.5 text-xs font-medium text-slate-600">
          Pagina {currentPage} de {totalPages}
        </span>
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
          className="flex items-center gap-1 px-3 py-1.5 text-xs border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Siguiente
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
};

// =============================================================================
// PENDING REVIEW PANEL
// =============================================================================
const PendingReviewPanel = ({ flash }) => {
  const {
    fetchPending, approvePending, approvePendingBulk, rejectPending,
    loading,
  } = useViatorExperiences();

  const [items, setItems] = useState([]);
  const [newCount, setNewCount] = useState(0);
  const [includeResolved, setIncludeResolved] = useState(false);
  const [busyCode, setBusyCode] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [minPrice, setMinPrice] = useState(''); // USD, '' = open-ended
  const [maxPrice, setMaxPrice] = useState('');

  const [editItem, setEditItem] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState(null);

  const load = useCallback(async () => {
    setLocalError(null);
    try {
      const res = await fetchPending({ includeResolved });
      setItems(res?.pending || []);
      setNewCount(res?.new_count || 0);
    } catch (err) {
      setItems([]);
      setLocalError(err.message || 'No se pudieron cargar las experiencias por revisar.');
    }
  }, [fetchPending, includeResolved]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setCurrentPage(1); }, [includeResolved, minPrice, maxPrice]);

  // Price range filters on the USD price the admin sees (backend already
  // converts price_from to USD before serving). Rows without a price are hidden
  // while a range is active, since they can't fall inside it.
  const min = minPrice.trim() === '' ? null : Number(minPrice);
  const max = maxPrice.trim() === '' ? null : Number(maxPrice);
  const priceActive = (min != null && !Number.isNaN(min)) || (max != null && !Number.isNaN(max));

  const filteredItems = priceActive
    ? items.filter((it) => {
        if (it.price_from == null) return false;
        const p = Number(it.price_from);
        if (min != null && !Number.isNaN(min) && p < min) return false;
        if (max != null && !Number.isNaN(max) && p > max) return false;
        return true;
      })
    : items;

  const totalPages = Math.ceil(filteredItems.length / ITEMS_PER_PAGE) || 1;
  const paginatedItems = filteredItems.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  // "Aprobar todas" operates on what's in view, so it respects the price filter.
  const newInView = filteredItems.filter((it) => it.status === 'new');

  const removeFromView = (code) => {
    setItems((prev) => prev.filter((it) => it.product_code !== code));
  };

  const handleApprove = async (item) => {
    setBusyCode(item.product_code);
    try {
      const res = await approvePending(item.product_code);
      if (res?.skipped) {
        flash('success', `Ya estaba importada: ${item.title}`);
      } else {
        // flash('success', `Aprobada e insertada: ${item.title}`);
      }
      if (!includeResolved) removeFromView(item.product_code);
      else load();
      setNewCount((n) => Math.max(0, n - 1));
    } catch (err) {
      flash('error', err.message || 'No se pudo aprobar la experiencia.');
    } finally {
      setBusyCode(null);
    }
  };

  const handleReject = async (item) => {
    setBusyCode(item.product_code);
    try {
      await rejectPending(item.product_code);
      // flash('success', `Rechazada: ${item.title}`);
      if (!includeResolved) removeFromView(item.product_code);
      else load();
      setNewCount((n) => Math.max(0, n - 1));
    } catch (err) {
      flash('error', err.message || 'No se pudo rechazar la experiencia.');
    } finally {
      setBusyCode(null);
    }
  };

  const handleApproveAll = async () => {
    const codes = newInView.map((it) => it.product_code);
    if (codes.length === 0) return;
    setBulkBusy(true);
    try {
      const res = await approvePendingBulk(codes);
      flash('success', `Aprobadas ${res?.approved ?? 0}, omitidas ${res?.skipped ?? 0}, con error ${res?.failed ?? 0}.`);
      load();
    } catch (err) {
      flash('error', err.message || 'No se pudo aprobar en lote.');
    } finally {
      setBulkBusy(false);
    }
  };

  const handleEditApprove = async (payload) => {
    if (!editItem) return;
    setSavingEdit(true);
    setEditError(null);
    try {
      await approvePending(editItem.product_code, payload);
      flash('success', `Aprobada con cambios: ${payload.title}`);
      if (!includeResolved) removeFromView(editItem.product_code);
      else load();
      setNewCount((n) => Math.max(0, n - 1));
      setEditItem(null);
    } catch (err) {
      setEditError(err.message || 'No se pudo aprobar la experiencia.');
    } finally {
      setSavingEdit(false);
    }
  };

  const statusBadge = (status) => {
    if (status === 'imported') return <span className="px-2 py-0.5 bg-emerald-500 text-white text-[10px] font-bold rounded-full">Importada</span>;
    if (status === 'rejected') return <span className="px-2 py-0.5 bg-slate-400 text-white text-[10px] font-bold rounded-full">Rechazada</span>;
    return <span className="px-2 py-0.5 bg-amber-500 text-white text-[10px] font-bold rounded-full">Nueva</span>;
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="text-sm text-slate-600 flex items-center gap-2 flex-wrap">
          <ClipboardCheck className="h-4 w-4 text-violet-500" />
          <span><strong>{newCount}</strong> por revisar</span>
          <span className="text-xs text-slate-400 font-mono"> desde unapproved_viator_experiences</span>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-xs text-slate-600">
            <input type="checkbox" checked={includeResolved} onChange={(e) => setIncludeResolved(e.target.checked)} className="h-3.5 w-3.5 rounded text-violet-600 focus:ring-violet-500" />
            Mostrar resueltas
          </label>
          <button onClick={load} disabled={loading} className="flex items-center gap-2 px-3 py-2 text-sm border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 disabled:opacity-50">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /><span className="hidden sm:inline">Actualizar</span>
          </button>
          <button onClick={handleApproveAll} disabled={bulkBusy || newInView.length === 0} className="flex items-center gap-2 px-4 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-slate-300">
            {bulkBusy ? <Loader2 className="animate-spin h-4 w-4" /> : <ListChecks className="h-4 w-4" />}
            <span>{priceActive ? `Aprobar ${newInView.length}` : 'Aprobar todas'}</span>
          </button>
        </div>
      </div>

      {/* Price range filter (USD) */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500">
          <DollarSign className="h-4 w-4 text-emerald-500" />
          <span>Rango de precio (USD):</span>
        </div>
        <input
          type="number"
          min="0"
          step="0.01"
          inputMode="decimal"
          placeholder="Min"
          value={minPrice}
          onChange={(e) => setMinPrice(e.target.value)}
          className="w-24 text-sm border border-slate-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
        />
        <span className="text-slate-400">—</span>
        <input
          type="number"
          min="0"
          step="0.01"
          inputMode="decimal"
          placeholder="Max"
          value={maxPrice}
          onChange={(e) => setMaxPrice(e.target.value)}
          className="w-24 text-sm border border-slate-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
        />
        {priceActive && (
          <>
            <span className="text-xs text-slate-500">{filteredItems.length} resultado(s)</span>
            <button
              onClick={() => { setMinPrice(''); setMaxPrice(''); }}
              className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
            >
              <X className="h-3.5 w-3.5" />Limpiar
            </button>
          </>
        )}
      </div>

      {localError && (
        <div className="mb-4 p-3 rounded-lg flex items-center gap-2 border bg-red-50 border-red-200 text-red-800">
          <AlertCircle className="h-5 w-5" /><span className="text-sm">{localError}</span>
        </div>
      )}

      {paginatedItems.length > 0 ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {paginatedItems.map((it) => {
              const acting = busyCode === it.product_code;
              const isNew = it.status === 'new';
              return (
                <div key={it.product_code} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex flex-col hover:shadow-md transition-shadow">
                  <div className="h-36 bg-slate-100 relative">
                    {it.cover_image_url ? (
                      <img src={it.cover_image_url} alt={it.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-300"><Compass className="h-10 w-10" /></div>
                    )}
                    <div className="absolute top-2 left-2 flex gap-1">{statusBadge(it.status)}</div>
                  </div>
                  <div className="p-4 flex-1 flex flex-col">
                    <h3 className="font-semibold text-slate-800 text-sm line-clamp-2">{it.title}</h3>
                    <p className="text-[11px] text-slate-400 font-mono mt-0.5">{it.product_code}</p>
                    <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-xs text-slate-600">
                      {it.rating != null && <span className="flex items-center gap-1"><Star className="h-3 w-3 text-amber-500" />{it.rating} ({it.review_count ?? 0})</span>}
                      {it.price_from != null && <span className="flex items-center gap-1"><DollarSign className="h-3 w-3 text-emerald-500" />{it.price_currency || 'USD'} {Number(it.price_from).toFixed(2)}</span>}
                      {it.location_city && <span className="flex items-center gap-1"><MapPin className="h-3 w-3 text-violet-500" />{it.location_city}</span>}
                      {it.category && <span className="flex items-center gap-1"><Tag className="h-3 w-3 text-indigo-500" />{categoryLabel(it.category)}</span>}
                    </div>
                    {it.short_description && <p className="text-xs text-slate-500 mt-2 line-clamp-2">{it.short_description}</p>}
                    <div className="mt-auto pt-3 flex gap-2">
                      <button onClick={() => handleApprove(it)} disabled={acting || !isNew} className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 disabled:opacity-40">
                        {acting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ThumbsUp className="h-3.5 w-3.5" />}Aprobar
                      </button>
                      <button onClick={() => { setEditError(null); setEditItem(it); }} disabled={acting || !isNew} className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs bg-violet-100 text-violet-700 rounded-lg hover:bg-violet-200 disabled:opacity-40">
                        <Edit2 className="h-3.5 w-3.5" />Editar
                      </button>
                      <button onClick={() => handleReject(it)} disabled={acting || !isNew} className="flex items-center justify-center px-2 py-1.5 text-xs bg-red-100 text-red-700 rounded-lg hover:bg-red-200 disabled:opacity-40">
                        <XCircle className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={filteredItems.length}
            onPageChange={setCurrentPage}
          />
        </>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 py-16 text-center">
          {loading ? (
            <><Loader2 className="h-10 w-10 text-violet-500 mx-auto mb-4 animate-spin" /><p className="text-slate-600">Cargando cola de revision...</p></>
          ) : priceActive && items.length > 0 ? (
            <>
              <DollarSign className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700">Ninguna experiencia en ese rango de precio</h3>
              <p className="text-sm text-slate-500 mt-1">Ajusta el rango o limpia el filtro para ver todas.</p>
            </>
          ) : (
            <>
              <Inbox className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700">No hay experiencias por revisar</h3>
              <p className="text-sm text-slate-500 mt-1">Ejecuta el scraper de experiencias para poblar la cola.</p>
            </>
          )}
        </div>
      )}

      <ExperienceFormDialog
        isOpen={!!editItem}
        mode="add"
        initial={editItem}
        onClose={() => !savingEdit && setEditItem(null)}
        onSubmit={handleEditApprove}
        saving={savingEdit}
        error={editError}
      />
    </div>
  );
};

// =============================================================================
// SCRAPE QUEUE PANEL (scraped_viator_indexes monitoring + discard)
// =============================================================================
const ScrapeQueuePanel = ({ flash }) => {
  const {
    fetchScrapeQueueStats, fetchScrapeQueue,
    deleteScrapeQueueEntry, clearScrapeQueue,
    loading,
  } = useViatorExperiences();

  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [showScraped, setShowScraped] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [clearBusy, setClearBusy] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [search, setSearch] = useState('');            // NEW
  const [currentPage, setCurrentPage] = useState(1);   // NEW

  const loadStats = useCallback(async () => {
    try {
      const s = await fetchScrapeQueueStats();
      setStats(s);
    } catch (err) {
      flash('error', err.message || 'No se pudieron cargar las estadisticas.');
    }
  }, [fetchScrapeQueueStats, flash]);

  const loadItems = useCallback(async () => {
    setLocalError(null);
    try {
      const res = await fetchScrapeQueue({
        scraped: showScraped || null,
        limit: 200,
      });
      setItems(res?.items || []);
    } catch (err) {
      setLocalError(err.message || 'No se pudo cargar la cola de scrapeo.');
    }
  }, [fetchScrapeQueue, showScraped]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadItems(); }, [loadItems]);

  // NEW — reset to page 1 whenever the result set changes shape
  useEffect(() => { setCurrentPage(1); }, [search, showScraped]);

  const query = search.trim().toLowerCase();
  const filteredItems = query
    ? items.filter((it) =>
      (it.viator_index || '').toLowerCase().includes(query) ||
      String(it.id).includes(query) ||
      (it.date || '').includes(query))
    : items;

  const totalPages = Math.ceil(filteredItems.length / ITEMS_PER_PAGE) || 1;
  const paginatedItems = filteredItems.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  // NEW — clamp if deletions shrink the list below the current page
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  const handleDelete = async (id) => {
    setBusyId(id);
    try {
      await deleteScrapeQueueEntry(id);
      flash('success', `Entrada #${id} eliminada.`);
      setItems((prev) => prev.filter((it) => it.id !== id));
      loadStats();
    } catch (err) {
      flash('error', err.message || 'No se pudo eliminar la entrada.');
    } finally {
      setBusyId(null);
    }
  };

  const handleClear = async () => {
    setClearBusy(true);
    try {
      const res = await clearScrapeQueue();
      flash('success', `${res?.deleted ?? 0} entradas eliminadas de la cola.`);
      setItems([]);
      loadStats();
      setShowClearConfirm(false);
    } catch (err) {
      flash('error', err.message || 'No se pudo vaciar la cola.');
    } finally {
      setClearBusy(false);
    }
  };

  return (
    <div>
      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <div className="bg-white rounded-lg border border-slate-200 p-3 text-center">
            <p className="text-2xl font-bold text-slate-800">{stats.total}</p>
            <p className="text-xs text-slate-500">Total URLs</p>
          </div>
          <div className="bg-white rounded-lg border border-amber-200 p-3 text-center">
            <p className="text-2xl font-bold text-amber-600">{stats.unscraped}</p>
            <p className="text-xs text-amber-600">Pendientes</p>
          </div>
          <div className="bg-white rounded-lg border border-emerald-200 p-3 text-center">
            <p className="text-2xl font-bold text-emerald-600">{stats.scraped}</p>
            <p className="text-xs text-emerald-600">Scrapeadas</p>
          </div>
          <div className="bg-white rounded-lg border border-violet-200 p-3 text-center">
            <p className="text-2xl font-bold text-violet-600">{stats.today_count}</p>
            <p className="text-xs text-violet-600">Hoy</p>
          </div>
        </div>
      )}
      {/* Search */}
      <div className="relative mb-4">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-4 w-4 text-violet-400" />
        </div>
        <input
          type="text"
          placeholder="Buscar por URL, ID o fecha..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-9 pr-10 py-2.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Filter + Refresh + Clear */}
      <div className="flex items-center justify-between gap-3 mb-4">
        <label className="flex items-center gap-1.5 text-xs text-slate-600">
          <input
            type="checkbox"
            checked={showScraped}
            onChange={(e) => setShowScraped(e.target.checked)}
            className="h-3.5 w-3.5 rounded text-violet-600 focus:ring-violet-500"
          />
          Mostrar ya scrapeadas
        </label>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { loadStats(); loadItems(); }}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 text-sm border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Actualizar</span>
          </button>
          {showClearConfirm ? (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowClearConfirm(false)}
                className="px-3 py-2 text-xs border border-slate-300 text-slate-600 rounded-lg hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleClear}
                disabled={clearBusy}
                className="flex items-center gap-1 px-3 py-2 text-xs bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-red-300"
              >
                {clearBusy ? <Loader2 className="animate-spin h-3 w-3" /> : <Eraser className="h-3 w-3" />}
                Confirmar vaciar
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowClearConfirm(true)}
              className="flex items-center gap-1 px-3 py-2 text-xs border border-red-300 text-red-600 rounded-lg hover:bg-red-50"
            >
              <Eraser className="h-3.5 w-3.5" />
              Vaciar cola
            </button>
          )}
        </div>
      </div>

      {localError && (
        <div className="mb-4 p-3 rounded-lg flex items-center gap-2 border bg-red-50 border-red-200 text-red-800">
          <AlertCircle className="h-5 w-5" /><span className="text-sm">{localError}</span>
        </div>
      )}

      {/* Table */}
      {filteredItems.length > 0 ? (
        <>
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
                  <tr>
                    <th className="px-4 py-2 text-left">ID</th>
                    <th className="px-4 py-2 text-left">URL</th>
                    <th className="px-4 py-2 text-left">Fecha</th>
                    <th className="px-4 py-2 text-center">Estado</th>
                    <th className="px-4 py-2 text-center">Accion</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {paginatedItems.map((it) => (
                    <tr key={it.id} className="hover:bg-slate-50">
                      <td className="px-4 py-2 text-xs font-mono text-slate-500">{it.id}</td>
                      <td className="px-4 py-2 max-w-xs">
                        <a href={it.viator_index} target="_blank" rel="noreferrer" className="text-violet-600 hover:underline text-xs truncate block">
                          {it.viator_index}
                        </a>
                      </td>
                      <td className="px-4 py-2 text-xs text-slate-500">{it.date} {it.time}</td>
                      <td className="px-4 py-2 text-center">
                        {it.scraped ? (
                          <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
                            <CheckSquare className="h-3.5 w-3.5" /> Scrapeada
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-amber-600">
                            <Square className="h-3.5 w-3.5" /> Pendiente
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-center">
                        <button
                          onClick={() => handleDelete(it.id)}
                          disabled={busyId === it.id}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-red-100 text-red-600 rounded hover:bg-red-200 disabled:opacity-40"
                        >
                          {busyId === it.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                          Descartar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={filteredItems.length}
            onPageChange={setCurrentPage}
          />
        </>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 py-16 text-center">
          {loading ? (
            <><Loader2 className="h-10 w-10 text-violet-500 mx-auto mb-4 animate-spin" /><p className="text-slate-600">Cargando cola de scrapeo...</p></>
          ) : query ? (
            <>
              <Search className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700">Sin resultados</h3>
              <p className="text-sm text-slate-500 mt-1">Ningun URL coincide con "{search}".</p>
            </>
          ) : (
            <>
              <Database className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700">Cola de scrapeo vacia</h3>
              <p className="text-sm text-slate-500 mt-1">Ejecuta viator-index-scraper para poblar la cola.</p>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// =============================================================================
// MAIN SECTION
// =============================================================================
export default function ViatorExperiencesPage() {
  const {
    fetchExperiences, createExperience, updateExperience, deleteExperience,
    loading, error, clearError,
  } = useViatorExperiences();

  const [experiences, setExperiences] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState(''); // '' = todas
  const [banner, setBanner] = useState(null);
  const [tab, setTab] = useState('curated'); // 'curated' | 'pending' | 'queue'

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState('add');
  const [formInitial, setFormInitial] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const [detailExp, setDetailExp] = useState(null);
  const [deleteExp, setDeleteExp] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [savingCategoryId, setSavingCategoryId] = useState(null);

  const load = useCallback(async (search = searchTerm, category = categoryFilter) => {
    try {
      clearError();
      const result = await fetchExperiences({
        search: search.trim() || undefined,
        category: category || undefined,
        limit: 200,
      });
      setExperiences(result?.experiences || []);
      setTotalCount(result?.total_count || 0);
    } catch (err) {
      setExperiences([]);
      setTotalCount(0);
    }
  }, [fetchExperiences, clearError, searchTerm, categoryFilter]);

  useEffect(() => { load('', ''); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const t = setTimeout(() => { load(searchTerm, categoryFilter); }, 450);
    return () => clearTimeout(t);
  }, [searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  // Category is a discrete choice — reload immediately (skip the initial mount,
  // which the load('') effect above already covers).
  const didMountCategory = useRef(false);
  useEffect(() => {
    if (!didMountCategory.current) { didMountCategory.current = true; return; }
    load(searchTerm, categoryFilter);
  }, [categoryFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const flash = (type, text) => {
    setBanner({ type, text });
    setTimeout(() => setBanner(null), 4000);
  };

  const openAdd = () => { setFormMode('add'); setFormInitial(null); setFormError(null); setFormOpen(true); };
  const openEdit = (exp) => { setFormMode('edit'); setFormInitial(exp); setFormError(null); setFormOpen(true); };

  const handleSubmit = async (payload) => {
    setSaving(true);
    setFormError(null);
    try {
      if (formMode === 'edit' && formInitial) {
        const updated = await updateExperience(formInitial.experience_id || formInitial.product_code, payload);
        setExperiences((prev) => prev.map((e) => (e.experience_id === updated.experience_id ? updated : e)));
        flash('success', 'Experiencia actualizada correctamente.');
      } else {
        const created = await createExperience(payload);
        setExperiences((prev) => [created, ...prev]);
        setTotalCount((n) => n + 1);
        flash('success', 'Experiencia creada correctamente.');
      }
      setFormOpen(false);
    } catch (err) {
      setFormError(err.message || 'No se pudo guardar la experiencia.');
    } finally {
      setSaving(false);
    }
  };

  // Inline category change from a card. Reuses the full-replace update (same as
  // saving the edit form with only the category changed) so behavior stays
  // consistent with the form. If a category filter is active and the row no
  // longer matches, drop it from the visible list.
  const changeCategory = async (exp, newCategory) => {
    const id = exp.experience_id || exp.product_code;
    const next = newCategory || '';
    if (next === (exp.category || '')) return;
    setSavingCategoryId(id);
    try {
      const payload = { ...formToPayload(experienceToForm(exp)), category: next || null };
      const updated = await updateExperience(id, payload);
      setExperiences((list) => {
        const mapped = list.map((e) => (e.experience_id === updated.experience_id ? updated : e));
        return categoryFilter
          ? mapped.filter((e) => (e.category || '') === categoryFilter)
          : mapped;
      });
      if (categoryFilter && (updated.category || '') !== categoryFilter) {
        setTotalCount((n) => Math.max(0, n - 1));
      }
      flash('success', `Categoria actualizada a "${next ? categoryLabel(next) : 'sin categoria'}".`);
    } catch (err) {
      flash('error', err.message || 'No se pudo actualizar la categoria.');
    } finally {
      setSavingCategoryId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteExp) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteExperience(deleteExp.experience_id || deleteExp.product_code);
      setExperiences((prev) => prev.filter((e) => e.experience_id !== deleteExp.experience_id));
      setTotalCount((n) => Math.max(0, n - 1));
      flash('success', 'Experiencia eliminada.');
      setDeleteExp(null);
    } catch (err) {
      setDeleteError(err.message || 'No se pudo eliminar.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-violet-50">
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-r from-violet-600 to-indigo-600 flex items-center justify-center">
                <Compass className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-800">Experiencias Viator</h2>
                <p className="text-xs text-slate-500">{totalCount} experiencia(s) curada(s)</p>
              </div>
            </div>
            {tab === 'curated' && (
              <div className="flex items-center gap-2">
                <button onClick={() => load(searchTerm)} disabled={loading} className="flex items-center gap-2 px-3 py-2 text-sm border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 disabled:opacity-50">
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /><span className="hidden sm:inline">Actualizar</span>
                </button>
                <button onClick={openAdd} className="flex items-center gap-2 px-4 py-2 text-sm bg-violet-600 text-white rounded-lg hover:bg-violet-700">
                  <Plus className="h-4 w-4" /><span>Agregar</span>
                </button>
              </div>
            )}
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 mt-4 border-b border-slate-200">
            <button
              onClick={() => setTab('curated')}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === 'curated' ? 'border-violet-600 text-violet-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            >
              <Compass className="h-4 w-4" />Curadas
            </button>
            <button
              onClick={() => setTab('pending')}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === 'pending' ? 'border-violet-600 text-violet-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            >
              <ClipboardCheck className="h-4 w-4" />Por revisar
            </button>
            <button
              onClick={() => setTab('queue')}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === 'queue' ? 'border-violet-600 text-violet-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            >
              <Link2 className="h-4 w-4" />Scrape Queue
            </button>
          </div>

          {tab === 'curated' && (
            <div className="mt-4 flex flex-col sm:flex-row gap-2">
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-violet-400" />
                </div>
                <input
                  type="text"
                  placeholder="Buscar por titulo, codigo o municipio..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-10 py-2.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                />
                {loading && <div className="absolute inset-y-0 right-0 pr-3 flex items-center"><Loader2 className="h-4 w-4 animate-spin text-violet-500" /></div>}
              </div>
              <div className="relative sm:w-56">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Tag className="h-4 w-4 text-violet-400" />
                </div>
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="w-full pl-9 pr-8 py-2.5 text-sm border border-slate-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 appearance-none"
                >
                  <option value="">Todas las categorias</option>
                  {CATEGORY_OPTIONS.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 pr-2 flex items-center pointer-events-none">
                  <ChevronRight className="h-4 w-4 text-slate-400 rotate-90" />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 py-4 sm:py-8">
        {banner && (
          <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 border ${banner.type === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-red-50 border-red-200 text-red-800'}`}>
            {banner.type === 'success' ? <CheckCircle className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
            <span className="text-sm">{banner.text}</span>
          </div>
        )}
        {error && !banner && tab === 'curated' && (
          <div className="mb-4 p-3 rounded-lg flex items-center gap-2 border bg-red-50 border-red-200 text-red-800">
            <AlertCircle className="h-5 w-5" /><span className="text-sm">{error}</span>
          </div>
        )}

        {tab === 'pending' && <PendingReviewPanel flash={flash} />}
        {tab === 'queue' && <ScrapeQueuePanel flash={flash} />}

        {tab === 'curated' && (experiences.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {experiences.map((exp) => (
              <div key={exp.experience_id || exp.product_code} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex flex-col hover:shadow-md transition-shadow">
                <div className="h-36 bg-slate-100 relative">
                  {exp.cover_image_url ? (
                    <img src={exp.cover_image_url} alt={exp.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-300"><Compass className="h-10 w-10" /></div>
                  )}
                  <div className="absolute top-2 left-2 flex gap-1">
                    {exp.is_featured && <span className="px-2 py-0.5 bg-amber-500 text-white text-[10px] font-bold rounded-full flex items-center gap-1"><Sparkles className="h-3 w-3" />Destacado</span>}
                    <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${exp.is_active ? 'bg-emerald-500 text-white' : 'bg-slate-400 text-white'}`}>{exp.is_active ? 'Activo' : 'Inactivo'}</span>
                  </div>
                </div>
                <div className="p-4 flex-1 flex flex-col">
                  <h3 className="font-semibold text-slate-800 text-sm line-clamp-2">{exp.title}</h3>
                  <p className="text-[11px] text-slate-400 font-mono mt-0.5">{exp.product_code}</p>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-slate-600">
                    {exp.rating != null && <span className="flex items-center gap-1"><Star className="h-3 w-3 text-amber-500" />{exp.rating} ({exp.review_count ?? 0})</span>}
                    {exp.price_from != null && <span className="flex items-center gap-1"><DollarSign className="h-3 w-3 text-emerald-500" />{exp.price_currency || 'USD'} {Number(exp.price_from).toFixed(2)}</span>}
                    {exp.location_city && <span className="flex items-center gap-1"><MapPin className="h-3 w-3 text-violet-500" />{exp.location_city}</span>}
                    <span className="flex items-center gap-1">
                      <Tag className="h-3 w-3 text-indigo-500" />
                      <select
                        value={exp.category || ''}
                        onChange={(e) => changeCategory(exp, e.target.value)}
                        disabled={savingCategoryId === (exp.experience_id || exp.product_code)}
                        title="Cambiar categoria"
                        className="text-xs border border-slate-200 rounded px-1 py-0.5 bg-white text-slate-600 focus:outline-none focus:ring-1 focus:ring-violet-500 focus:border-violet-500 disabled:opacity-50 cursor-pointer"
                      >
                        <option value="">Sin categoria</option>
                        {CATEGORY_OPTIONS.map((c) => (
                          <option key={c.value} value={c.value}>{c.label}</option>
                        ))}
                        {exp.category && !CATEGORY_OPTIONS.some((c) => c.value === exp.category) && (
                          <option value={exp.category}>{exp.category}</option>
                        )}
                      </select>
                      {savingCategoryId === (exp.experience_id || exp.product_code) && (
                        <Loader2 className="h-3 w-3 animate-spin text-violet-500" />
                      )}
                    </span>
                  </div>
                  {exp.short_description && <p className="text-xs text-slate-500 mt-2 line-clamp-2">{exp.short_description}</p>}
                  <div className="mt-auto pt-3 flex gap-2">
                    <button onClick={() => setDetailExp(exp)} className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200"><Eye className="h-3.5 w-3.5" />Ver</button>
                    <button onClick={() => openEdit(exp)} className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs bg-violet-100 text-violet-700 rounded-lg hover:bg-violet-200"><Edit2 className="h-3.5 w-3.5" />Editar</button>
                    <button onClick={() => { setDeleteError(null); setDeleteExp(exp); }} className="flex items-center justify-center px-2 py-1.5 text-xs bg-red-100 text-red-700 rounded-lg hover:bg-red-200"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 py-16 text-center">
            {loading ? (
              <><Loader2 className="h-10 w-10 text-violet-500 mx-auto mb-4 animate-spin" /><p className="text-slate-600">Cargando experiencias...</p></>
            ) : (
              <>
                <Compass className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-700">No hay experiencias</h3>
                <p className="text-sm text-slate-500 mt-1">Agrega la primera experiencia Viator con el boton "Agregar".</p>
              </>
            )}
          </div>
        ))}
      </main>

      <ExperienceFormDialog
        isOpen={formOpen}
        mode={formMode}
        initial={formInitial}
        onClose={() => !saving && setFormOpen(false)}
        onSubmit={handleSubmit}
        saving={saving}
        error={formError}
      />
      <ExperienceDetailDialog isOpen={!!detailExp} exp={detailExp} onClose={() => setDetailExp(null)} />
      <DeleteDialog isOpen={!!deleteExp} exp={deleteExp} onClose={() => !deleting && setDeleteExp(null)} onConfirm={handleDelete} deleting={deleting} error={deleteError} />
    </div>
  );
}